/* eslint-disable import/no-unresolved */
/* eslint-disable camelcase */

import { useSelector } from 'react-redux';

import { getUnrealizedCashFlow } from 'components/balance/helpers';
import {
  getBalanceEquity,
  getBalanceUnrealizedPnl,
} from 'components/holdings_balance/selectorHelper';
import Render from 'components/render';
import {
  ASSET_SYMBOL,
  DETO_PRECISION,
  SPOT_PRICE,
  TOTAL_EQUITY_DECIMAL,
} from 'constants/constants';
import { LOCALES } from 'constants/dateTime';
import { CURRENCY_TYPE } from 'constants/enums';
import decorateBalance from 'decorators/balance';
import { getUnrealisedPnlFromApi } from 'helpers/authFallbackHelpers';
import { formatFiat, prettifyINR, prettifyUSD } from 'helpers/currency';
import {
  addZeroesUntilCorrectPrecision,
  convertExponentialToDecimal,
  convertFromUSDtoINR,
  cropAfterDecimals,
  indianNumberCommaSeparator,
  numberCommaSeparator,
  sortBalancesBySortPriority,
} from 'helpers/utils';
import { getValidBalance } from 'helpers/wallet';
import subscribe from 'HOC/subscribe';
import { useCheckShouldShowContentForAccountEquity } from 'hooks/componentHooks/useCheckShouldShowEquity';
import {
  allOpenPositionsSelector,
  trackerAssetsHoldingsSelector,
} from 'selectors/holdingsSelectors';
import { inrConversionRateSelector } from 'selectors/otherSelectors';
import {
  openPositionMarkPriceSelectorById,
  walletSpotPriceBySymbol,
} from 'selectors/priceSelectors';
import { referralBonusSelector } from 'selectors/referralBonusSelector';
import { selectIsAuthChannelFailed } from 'selectors/user';
import {
  enableWalletsSelector,
  portfolioMarginSelector,
  roboTradingEquitySelector,
} from 'selectors/walletSelector';
import { useAppSelector } from 'storeHooks';
import INR from 'styles/assets/coins/INR';
import Box from 'UIKit/Box';
import { LoadingSkeleton } from 'UIKit/LoadingSkeleton';

const getSpotPrice = (assetSymbol, spotPriceFunc) => {
  let spotPrice = 0;
  switch (assetSymbol) {
    case 'BTC':
      spotPrice = Number(spotPriceFunc(`.DEXBT${ASSET_SYMBOL}`));
      break;
    case ASSET_SYMBOL:
      spotPrice = SPOT_PRICE[ASSET_SYMBOL];
      break;
    case 'INR':
      spotPrice = SPOT_PRICE.INR;
      break;
    default:
      spotPrice = Number(spotPriceFunc(`.DE${assetSymbol}${ASSET_SYMBOL}`));
      break;
  }
  return spotPrice;
};

const getTotalTrackerEquity = (
  trackerHoldings,
  getMarkPrice,
  isAuthChannelDisconnected
) => {
  return trackerHoldings.reduce((acc, position) => {
    const openPositionMarkObj = getMarkPrice(String(position?.product?.id));
    const exitPrice = openPositionMarkObj?.mark_price || 0;

    let equity =
      Number(position.size) * Number(position.product.contract_value) * Number(exitPrice);

    if (isAuthChannelDisconnected) {
      const buyPrice =
        Number(position.size) *
        Number(position.product.contract_value) *
        Number(position.entry_price);
      const upnlFromApi = getUnrealisedPnlFromApi(position);
      equity = buyPrice + upnlFromApi;
    }
    return acc + Number(equity);
  }, 0);
};

/**
 *
 * @param {Array} balanceArray
 * @param {Array} openPosition
 * @param {Function} getMarkPrice
 * @param {Function} spotPriceFunc
 * @param {Number} positions_upl
 * @param {Number} [portfolioEnabledAsset]
 * @returns {Number} totalEquity
 */
const getTotalTradingEquity = (
  balanceArray,
  openPositions,
  getMarkPrice,
  spotPriceFunc,
  portfolio_upl,
  portfolioEnabledAsset,
  isAuthChannelDisconnected
) =>
  balanceArray.reduce((acc, coinBalance) => {
    const assetSymbol = coinBalance.asset.symbol;
    const spotPrice = getValidBalance(getSpotPrice(assetSymbol, spotPriceFunc));
    const balanceEquity = getValidBalance(
      getBalanceEquity(
        coinBalance,
        openPositions,
        getMarkPrice,
        portfolio_upl,
        portfolioEnabledAsset,
        isAuthChannelDisconnected
      )
    );
    const value = acc + spotPrice * balanceEquity;

    return value;
  }, 0);

const getTotalTradingEquityInINR = (
  balanceArray,
  openPositions,
  getMarkPrice,
  portfolio_upl,
  portfolioEnabledAsset,
  inrConversionRate,
  isAuthChannelDisconnected
) => {
  return balanceArray.reduce((acc, coinBalance) => {
    const balanceInr = getValidBalance(coinBalance?.balance_inr ?? 0);
    const unrealisedCashflow = getValidBalance(
      getBalanceUnrealizedPnl(
        coinBalance?.asset?.symbol,
        openPositions,
        getMarkPrice,
        isAuthChannelDisconnected
      )
    );
    const portfolioCashflow = getValidBalance(
      portfolioEnabledAsset === coinBalance?.asset?.id ? Number(portfolio_upl) : 0
    );
    const value =
      acc + balanceInr + inrConversionRate * (portfolioCashflow + unrealisedCashflow);
    return value;
  }, 0);
};

const TotalEquity = subscribe(
  ({
    className,
    usdClassName,
    btcClassName,
    hideBTC = false,
    isInr = false,
    isForWalletIndianExchange = false,
    shouldPrettifyUSD = false,
    shouldPrettifyINR = false,
    includeReferralBonus = false,
    withTrackerEquity = false,
  }) => {
    const balance = useAppSelector(enableWalletsSelector);

    const balancesArray = sortBalancesBySortPriority(balance);

    const spotPriceFunc = symbol => walletSpotPriceBySymbol(symbol);
    const getMarkPrice = productId => openPositionMarkPriceSelectorById(productId);

    const openPositions = useSelector(allOpenPositionsSelector);
    const totalRoboStrategiesEquity = useSelector(roboTradingEquitySelector);
    const trackerHoldings = useSelector(trackerAssetsHoldingsSelector);

    const shouldShowContent = useCheckShouldShowContentForAccountEquity(
      openPositions,
      balance
    );

    const referralBonus = useAppSelector(referralBonusSelector);

    const isAuthChannelDisconnected = useAppSelector(selectIsAuthChannelFailed);

    const { positions_upl, asset_id: portfolioEnabledAsset } =
      portfolioMarginSelector().aggregate || {};

    const totalTrackerEquity = withTrackerEquity
      ? getTotalTrackerEquity(trackerHoldings, getMarkPrice, isAuthChannelDisconnected)
      : 0;

    const btcusdSpotPrice = walletSpotPriceBySymbol(`.DEXBT${ASSET_SYMBOL}`);
    const inrConversionRate = useSelector(inrConversionRateSelector);
    const usdBalanceObject = balance?.USD ?? {};
    const usdAssetMinPrecision = usdBalanceObject?.asset?.minimum_precision ?? 2;

    if (!shouldShowContent) {
      return <LoadingSkeleton height={24} width={50} animateSkeleton />;
    }

    const totalTradingEquity = getTotalTradingEquity(
      balancesArray,
      openPositions,
      getMarkPrice,
      spotPriceFunc,
      positions_upl,
      portfolioEnabledAsset,
      isAuthChannelDisconnected
    );

    const totalEquityInUsd = cropAfterDecimals(
      Number(totalRoboStrategiesEquity) +
        Number(totalTradingEquity) +
        Number(totalTrackerEquity),
      TOTAL_EQUITY_DECIMAL
    );

    const totalEquityInINR = cropAfterDecimals(
      convertExponentialToDecimal(totalEquityInUsd / inrConversionRate) +
        convertFromUSDtoINR(Number(referralBonus), inrConversionRate),
      TOTAL_EQUITY_DECIMAL
    );
    const totalEquityInBTC = cropAfterDecimals(
      convertExponentialToDecimal(totalEquityInUsd / btcusdSpotPrice),
      8
    );

    if (isForWalletIndianExchange && !isInr) {
      let totalEquity = 0;

      if (includeReferralBonus) {
        totalEquity += Number(referralBonus);
      }

      const amountUSD = cropAfterDecimals(
        addZeroesUntilCorrectPrecision(
          Number(totalTradingEquity) + Number(totalTrackerEquity) + Number(totalEquity),
          usdAssetMinPrecision
        ),
        usdAssetMinPrecision
      );

      return shouldPrettifyUSD
        ? prettifyUSD(amountUSD)
        : formatFiat(CURRENCY_TYPE.USD, amountUSD);
    }

    if (isForWalletIndianExchange && isInr) {
      let totalEquity = 0;
      if (includeReferralBonus) {
        totalEquity += Number(convertFromUSDtoINR(referralBonus, inrConversionRate));
      }
      const totalTradingEquityInINR = getTotalTradingEquityInINR(
        balancesArray,
        openPositions,
        getMarkPrice,
        positions_upl,
        portfolioEnabledAsset,
        inrConversionRate,
        isAuthChannelDisconnected
      );
      const totalTrackerEquityInINR = Number(
        convertFromUSDtoINR(totalTrackerEquity, inrConversionRate)
      );
      totalEquity += Number(totalTradingEquityInINR) + Number(totalTrackerEquityInINR);

      const amountINR = cropAfterDecimals(
        addZeroesUntilCorrectPrecision(totalEquity, usdAssetMinPrecision),
        usdAssetMinPrecision
      );

      return shouldPrettifyINR
        ? prettifyINR(amountINR)
        : formatFiat(CURRENCY_TYPE.INR, amountINR, LOCALES.INDIA);
    }

    if (isInr) {
      return (
        (<div className={className} data-palette="TotalEquity">
          <span className={usdClassName}>
            <INR />{' '}
            {Number.isNaN(Number(totalEquityInINR))
              ? '-'
              : indianNumberCommaSeparator(totalEquityInINR)}
          </span>{' '}
          <span className={usdClassName}>
            ${' '}
            {Number.isNaN(Number(totalEquityInUsd))
              ? '-'
              : numberCommaSeparator(totalEquityInUsd)}
          </span>
        </div>)
      );
    }

    return (
      (<div className={className} data-palette="TotalEquity">
        <span className={usdClassName}>
          ${' '}
          {Number.isNaN(Number(totalEquityInUsd))
            ? '-'
            : numberCommaSeparator(totalEquityInUsd)}
        </span>{' '}
        <Render when={!hideBTC}>
          <span className={btcClassName}>
            ~
            {Number.isNaN(Number(totalEquityInBTC))
              ? '-'
              : numberCommaSeparator(totalEquityInBTC)}{' '}
            BTC
          </span>
        </Render>
      </div>)
    );
  }
);

const UnrealisedCashflow = subscribe(
  ({ coinBalance, returnInInr = false, showFullPrecision = false, className }) => {
    const balances = useAppSelector(enableWalletsSelector);
    const balanceFormatted = decorateBalance(sortBalancesBySortPriority(balances));
    const openPositions = useAppSelector(allOpenPositionsSelector);
    const portfolioMarginData = portfolioMarginSelector();
    // eslint-disable-next-line camelcase
    const { positions_upl = 0, asset_id } = portfolioMarginData?.aggregate || {};
    const unrealizedCashFlow = getUnrealizedCashFlow(
      balanceFormatted,
      openPositions,
      positions_upl,
      asset_id
    );
    const inrConversionRate = useAppSelector(inrConversionRateSelector);

    const getPrecision = () => {
      if (showFullPrecision) return coinBalance?.asset?.precision;
      if (coinBalance?.asset?.symbol === 'DETO') return DETO_PRECISION;
      return coinBalance?.asset?.minimum_precision;
    };

    const getValue = () => {
      if (returnInInr) {
        return cropAfterDecimals(
          convertFromUSDtoINR(unrealizedCashFlow, inrConversionRate),
          getPrecision()
        );
      }

      return cropAfterDecimals(unrealizedCashFlow, getPrecision());
    };

    const displayValue = numberCommaSeparator(getValue());

    return (
      <Box as="span" className={className}>
        {displayValue}
      </Box>
    );
  }
);

export { getTotalTradingEquity, TotalEquity, UnrealisedCashflow, getTotalTrackerEquity };
